/**
 * EndlessWorld — the streaming generator behind endless mode.
 *
 * One instance owns the seed, the shared 3D occupancy, and the head-of-line
 * state, and generates segments strictly in order (segment i+1 grows from
 * segment i's exit pose — the continuity is the point; determinism per seed
 * is total because every random decision is label-derived from
 * `segment/${i}` / `island/${k}` families).
 *
 * Pipeline per segment: planSegment (void clauses + island anchors, claims
 * occupancy) → realize (open-path WFC over the same piece vocabulary, world
 * occupancy vetoing footprint extras) → assemble (open: no supports, no
 * goal, no solids) → buildIsland per crossing. The client asks for segments
 * ahead of the marble and culls behind it; culling releases every claim the
 * segment and its islands made, so memory tracks the streaming window, not
 * the distance travelled.
 */
import type {
  CurveSeg,
  Dir,
  LoopPath,
  Placement,
  Vec3,
} from "../../shared/types.ts";
import { rngFamily } from "../rng.ts";
import { buildRegistry, type PieceRegistry } from "../registry.ts";
import { pickStyle, type StyleField } from "../style.ts";
import type { ElevationPlan } from "../elevation.ts";
import type { Skeleton } from "../topology.ts";
import { realize } from "../wfc.ts";
import { assemble } from "../assemble.ts";
import type { DecorCatalog } from "../decor.ts";
import {
  applyXform,
  DIR_STEP,
  segLength,
  segPoint,
  turnLeft,
  turnRight,
  windowXform,
} from "../geom.ts";
import {
  type Affine,
  AFFINE_IDENTITY,
  affineApply,
  composeTwist,
  isIdentityAffine,
  quatRotate,
  type SegmentFrame,
} from "../../shared/frame.ts";
import { CHANNEL_CLEARANCE, Occupancy, OccupancyTx } from "./occupancy.ts";
import { scaleSeg, scaleVec, TRACK_SCALE } from "./scale.ts";
import {
  hasFlatContinuation,
  type HeadState,
  initialHeadState,
  PlannerStuck,
  planSegment,
  type SegmentPlan,
} from "./path.ts";
import { buildIsland, type Island } from "./islands.ts";
import { planIslandFurniture } from "./furniture.ts";
import { planVoidDecor } from "./voidDecor.ts";
import { segmentClimate } from "../climate.ts";
import { MOOD_CLAUSE_BIAS } from "./path.ts";

/** M21 climate × M24 temperament, one multiplicative record. */
function composeClauseBias(
  mood: string,
  temperament: { clauseBias: Record<string, number> },
): Record<string, number> {
  const a = MOOD_CLAUSE_BIAS[mood] ?? {};
  const b = temperament.clauseBias;
  const out: Record<string, number> = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = (out[k] ?? 1) * v;
  return out;
}

export interface EndlessSegment {
  index: number;
  /** Planning data in TRACK CELLS (the generator's abstract grid). */
  plan: SegmentPlan;
  /** Piece family per path segment (aligned with path.segments) — the
   * per-tile physics modifier channel (M11): the client maps families to
   * speed/gravity behavior at the rail-station level. */
  pieceFamilies: string[];
  /** Cell range per window (aligned with pieceFamilies/path.segments) —
   * lets tests and tools map plan cells to realized windows. */
  windowRanges: { start: number; len: number }[];
  /** Planner set-piece zone kind per path segment (or null). */
  windowMods: (string | null)[];
  /** Track placements in WORLD coordinates (×TRACK_SCALE, meshes scaled —
   * the slalom rule; islands/decor stay 1× on the world grid). */
  placements: Placement[];
  /** Open WORLD-space path through this segment, entry → exit (LoopPath
   * shape, but nothing wraps — the "loop" never closes). */
  path: LoopPath;
  islands: Island[];
  /** Braid alt LANE in WORLD coordinates (M17): the sub-assembled open
   * path over the braid's alt cells, branch port → branch port. The
   * client (and the drive harness) append plain channelProxies over it —
   * the alt lane is an ordinary channel; only the fork/merge junctions
   * need composite treatment (proxies.ts). Absent = no braid here. */
  altPath?: LoopPath;
  /** Floating void decor (M18b), WORLD coordinates — kept OUT of
   * `placements` so the proxy path structurally cannot see it (never
   * collides, never claims occupancy). */
  voidDecor: Placement[];
  /** Braid V3: this segment RESOLVES a fork opened in the named earlier
   * segment — the client keys per-racer lane draws on the FORK segment
   * so a rider's lane survives the boundary. */
  braidContOf?: number;
  /** Cumulative arc length of the line at this segment's entry. */
  startS: number;
  /** Presentation frame (M16 gravity reorientation): how this segment's
   * plan-space data maps into world space. Omitted = identity (no
   * reorient history). All plan/path/placement data above stays
   * plan-space +y-up regardless. */
  frame?: SegmentFrame;
}

const key = (x: number, z: number) => `${x},${z}`;

/** Live window: how many trailing segments keep their data + claims. The
 * client's render window (ahead + behind) must fit inside this. */
export const KEEP_SEGMENTS = 10;

/** Successor-probe labels: a segment commits only if its successor plans
 * under at least one of these; the successor retries them last. */
const PROBE_LABELS = ["probe", "probe/1", "probe/2", "probe/3"];

export class EndlessWorld {
  readonly seed: string;
  readonly style: StyleField;
  private readonly registry: PieceRegistry;
  private readonly catalog: DecorCatalog;
  private readonly occ = new Occupancy();
  private state: HeadState = initialHeadState();
  private segments: (EndlessSegment | null)[] = [];
  /** First index not yet culled (segments before it are tombstones). */
  private firstLive = 0;
  /** Cumulative arc length at the head — survives culling. */
  private nextStartS = 0;
  /** Cumulative presentation frame (M16) — reorient runs compose onto
   * it; as seed-pure as nextStartS (generation is strictly forward). */
  private frame: Affine = AFFINE_IDENTITY;
  /** Per-seed braid breadth knob (M17): 1..3, drawn once — some seeds
   * braid clean parallel sidings, some tangle three lanes out. The
   * planner draws each braid's actual breadth under this cap. */
  private readonly braidBias: number;

  constructor(seed: string, catalog: DecorCatalog = { packs: [] }) {
    this.seed = seed;
    this.catalog = catalog;
    this.registry = buildRegistry();
    // Same style family as finite mode: one seed, one theme, both modes.
    this.style = pickStyle(rngFamily(seed, "style"));
    this.braidBias = 1 + rngFamily(seed, "braid-breadth").int(0, 3);
  }

  /** Segment by index — generates forward as needed (never backward). */
  get(i: number): EndlessSegment {
    if (i < this.firstLive) {
      throw new Error(
        `segment ${i} already culled (firstLive ${this.firstLive})`,
      );
    }
    while (this.segments.length <= i) this.generateNext();
    return this.segments[i]!;
  }

  /** Number of segments generated so far. */
  get generatedCount(): number {
    return this.segments.length;
  }

  /** Occupancy cell count — bounded-memory checks in tests. */
  get occupancySize(): number {
    return this.occ.size;
  }

  /**
   * Culling is GENERATION-DRIVEN, never client-driven: when segment i is
   * generated, segment i − KEEP_SEGMENTS is released (occupancy claims of
   * its track and islands — an island's anchorage IS its birth segment) and
   * its data dropped. Pinning cull timing to generation keeps the stream a
   * pure function of the seed; if it followed the realtime marble instead,
   * whether an old claim still existed when a segment was planned would
   * depend on frame timing. The client sizes its render window under
   * KEEP_SEGMENTS and disposes scene groups on its own schedule — that's
   * rendering, not world state.
   */
  private cullBefore(index: number): void {
    for (
      let i = this.firstLive;
      i < Math.min(index, this.segments.length);
      i++
    ) {
      const seg = this.segments[i];
      if (!seg) continue;
      this.occ.release(`seg/${i}`);
      for (const isle of seg.islands) this.occ.release(isle.ref);
      this.segments[i] = null;
    }
    this.firstLive = Math.max(this.firstLive, index);
  }

  /** True when segment `index` plans under at least one probe label, on a
   * throwaway transaction stacked over `base`. */
  private successorPlans(
    index: number,
    state: HeadState,
    base: OccupancyTx,
  ): boolean {
    const rng = rngFamily(this.seed, `segment/${index}`);
    for (const label of PROBE_LABELS) {
      try {
        planSegment(
          rng.child(label),
          index,
          state,
          new OccupancyTx(base),
          this.registry,
          this.braidBias,
          composeClauseBias(
            segmentClimate(this.seed, index).mood,
            this.style.temperament,
          ),
        );
        return true;
      } catch (err) {
        if (!(err instanceof PlannerStuck)) throw err;
      }
    }
    return false;
  }

  private generateNext(): void {
    const i = this.segments.length;
    const segRng = rngFamily(this.seed, `segment/${i}`);
    // Transactional generation: plan AND realize inside one occupancy
    // transaction. WFC footprint extras (bend arc interiors, wave flanks…)
    // are world matter that only exists after realization — a segment
    // commits only if the exit corridor survives them; otherwise the whole
    // attempt is discarded and replanned under a fresh rng label. Dead ends
    // and retries are both deterministic per seed.
    let result:
      | {
        plan: ReturnType<typeof planSegment>["plan"];
        next: ReturnType<typeof planSegment>["next"];
        windows: ReturnType<typeof realize>;
        /** Braid alt-lane windows (M17, per braid since M19b) —
         * realized inside the same transaction; empty when the plan
         * carries no braid. */
        altWindowsOf: ReturnType<typeof realize>[];
        skeleton: Skeleton;
        elevation: ElevationPlan;
        tx: OccupancyTx;
      }
      | null = null;
    // Attempt order: the plain label, fresh retries, then the probe label
    // set — our predecessor proved at least one of those plans from our
    // exact entry state, so they're the near-guaranteed fallbacks.
    const labels = [
      "",
      "retry/1",
      "retry/2",
      "retry/3",
      "retry/4",
      "retry/5",
      ...PROBE_LABELS,
    ];
    for (const label of labels) {
      if (result) break;
      const tx = new OccupancyTx(this.occ);
      const rng = label === "" ? segRng : segRng.child(label);
      let planned;
      try {
        planned = planSegment(
          rng,
          i,
          this.state,
          tx,
          this.registry,
          this.braidBias,
          // M21+M24: the zone's base climate and the seed's temperament
          // lean the clause draw together.
          composeClauseBias(
            segmentClimate(this.seed, i).mood,
            this.style.temperament,
          ),
        );
      } catch (err) {
        if (err instanceof PlannerStuck) continue;
        throw err;
      }
      const { plan, next } = planned;
      const skeleton: Skeleton = {
        cells: plan.cells,
        crossings: [],
        helixLoops: [],
      };
      const elevation: ElevationPlan = {
        baseY: plan.cells.map((c) => c.baseY),
        reservations: plan.reservations,
        maxBaseY: Math.max(...plan.cells.map((c) => c.baseY)),
      };
      // Jump/propeller trajectory cells: approach (3 before the gap)
      // through landing (2 after) assume a STRAIGHT channel — a wave
      // window free-collapsing over them swings the channel laterally
      // and the ballistic marble misses the catch (found the hard way:
      // a wave landing edge-bounced the marble back into the void).
      const noLateral: [number, number][] = plan.mods
        .filter((m) => m.kind === "jump" || m.kind === "propeller")
        .map((m) => [m.start - 3, m.start + m.len + 2]);
      // Braid spans (M17) are flat runway: the kit's waves AND short
      // s-curves DIP the channel vertically, and a dip against a
      // junction port parks a slow marble on the crest at the deck lip
      // (grav-scan-21's alt rider stalled exactly there — first on a
      // wave, then on the s-curve the collapse picked instead). ±2 cells
      // of margin keeps the approach/exit flat as well. Kept separate
      // from the jump ranges so the braid rule can cover both families
      // without touching the priced jump-approach behavior.
      const braidFlat: [number, number][] = plan.braids.map(
        (b) => [b.start - 2, b.start + b.len + 2],
      );
      // Bump lips: bumps re-admit ONLY fully inside a single boost zone
      // (sprint runs are the only zones with ≥4 free flat cells) — the
      // crest launch is priced by the zone's speed, and elsewhere the
      // assisted marble lacks crest momentum.
      const boostRanges: [number, number][] = plan.mods
        .filter((m) => m.kind === "boost")
        .map((m) => [m.start, m.start + m.len]);
      const windows = realize(
        rng.child("wfc"),
        skeleton,
        elevation,
        this.style,
        this.registry,
        [],
        {
          open: true,
          extraFree: (x, z, baseY) =>
            tx.isFree(x, z, baseY, baseY + CHANNEL_CLEARANCE),
          // Physics era: bumps demand crest momentum — allowed only when
          // the window sits fully inside one boost zone (sprint runs),
          // and only the LIP tiers: a/b rise 0.5/1.0 world and the crest
          // curvature stays below the launch threshold at the boost
          // target (κ ≈ 0.375·R_world vs v²κ = g at 0.54). Tier c/d are
          // 2–3 world tall — genuine launch ramps that threw the marble
          // over the walls.
          exclude: (def, start) =>
            (def.family === "bump" &&
              (!(def.id === "bump-a" || def.id === "bump-b") ||
                !boostRanges.some(([a, b]) =>
                  start >= a && start + def.moves.length <= b
                ))) ||
            (def.family === "wave" && noLateral.some(([a, b]) =>
              start < b && a < start + def.moves.length
            )) ||
            ((def.family === "wave" || def.family === "s-curve") &&
              braidFlat.some(([a, b]) =>
                start < b && a < start + def.moves.length
              )),
        },
      );
      // Claim the extras into the transaction, then confirm the exit
      // corridor is still alive.
      for (const win of windows) {
        const cell = plan.cells[win.start];
        const xf = windowXform(cell.x, cell.z, cell.dirIn, 0);
        const windowSet = new Set<string>();
        for (let j = 0; j < win.len; j++) {
          const c = plan.cells[win.start + j];
          windowSet.add(key(c.x, c.z));
        }
        const baseY = cell.baseY;
        for (const [fx, fz] of win.def.footprint) {
          const w = applyXform(xf, [fx, 0, fz]);
          const wx = Math.round(w[0]);
          const wz = Math.round(w[2]);
          if (windowSet.has(key(wx, wz))) continue;
          // Reserved set-pieces' extras were claimed by the planner
          // already — not free there means ours, skip the duplicate.
          if (tx.isFree(wx, wz, baseY, baseY + CHANNEL_CLEARANCE)) {
            tx.claim(wx, wz, baseY, baseY + CHANNEL_CLEARANCE, `seg/${i}`);
          }
        }
      }
      // BRAID alt lane (M17): realize the alt sub-walk inside the SAME
      // transaction (its footprint extras are world matter too — they
      // must be claimed before the exit-corridor checks and the
      // successor probe run). No boost zones exist on the alt lane, so
      // bumps are excluded outright (the main realize's momentum rule).
      // Generalized over plan.braids (M19b): the planner still emits at
      // most one, but the realize is a loop — flipping that cap later is
      // a planner-side weight change with its own pin cycle. Braid 0
      // keeps the bare "braid-wfc" child label so existing seeds draw
      // byte-identically.
      const altWindowsOf: ReturnType<typeof realize>[] = [];
      let altWedged = false;
      for (const [bi, braid] of plan.braids.entries()) {
        const altSkeleton: Skeleton = {
          cells: braid.altCells,
          crossings: [],
          helixLoops: [],
        };
        const altElevation: ElevationPlan = {
          baseY: braid.altCells.map((c) => c.baseY),
          reservations: [],
          maxBaseY: Math.max(...braid.altCells.map((c) => c.baseY)),
        };
        let altWindows: ReturnType<typeof realize>;
        try {
          altWindows = realize(
            bi === 0 ? rng.child("braid-wfc") : rng.child(`braid-wfc/${bi}`),
            altSkeleton,
            altElevation,
            this.style,
            this.registry,
            [],
            {
              open: true,
              extraFree: (x, z, baseY) =>
                tx.isFree(x, z, baseY, baseY + CHANNEL_CLEARANCE),
              // No bumps (they demand crest momentum and the alt lane
              // has no boost zones) and no waves/s-curves (vertical dips
              // — the alt rider corners slowly, and a dip beside the
              // junction mouths parks it; V1 alt lanes are flat-floored).
              exclude: (def) =>
                def.family === "bump" || def.family === "wave" ||
                def.family === "s-curve",
            },
          );
        } catch (_err) {
          // A wedged alt collapse discards the whole attempt — same
          // contract as a planner dead end, deterministic per seed.
          altWedged = true;
          break;
        }
        altWindowsOf.push(altWindows);
        for (const win of altWindows) {
          const cell = braid.altCells[win.start];
          const xf = windowXform(cell.x, cell.z, cell.dirIn, 0);
          const windowSet = new Set<string>();
          for (let j = 0; j < win.len; j++) {
            const c = braid.altCells[win.start + j];
            windowSet.add(key(c.x, c.z));
          }
          const baseY = cell.baseY;
          for (const [fx, fz] of win.def.footprint) {
            const w = applyXform(xf, [fx, 0, fz]);
            const wx = Math.round(w[0]);
            const wz = Math.round(w[2]);
            if (windowSet.has(key(wx, wz))) continue;
            if (tx.isFree(wx, wz, baseY, baseY + CHANNEL_CLEARANCE)) {
              tx.claim(wx, wz, baseY, baseY + CHANNEL_CLEARANCE, `seg/${i}`);
            }
          }
        }
      }
      if (altWedged) continue;
      // Exit viability, two stages. First the cheap one: a flat corridor
      // must survive this attempt's claims (path, islands, canopy, WFC
      // extras). Then the real one: PLAN THE SUCCESSOR as probes on a
      // stacked transaction — no fixed-depth corridor check can rule out
      // every cul-de-sac, but "segment i+1 actually plans" can. Probes use
      // a fixed label set so the successor's own last-resort attempts
      // replay exactly what was validated here; the set (rather than one
      // label) keeps a single unlucky hypothetical route from vetoing a
      // good attempt.
      if (!hasFlatContinuation(tx, plan.exit, 6)) continue;
      if (!this.successorPlans(i + 1, next, tx)) continue;
      result = { plan, next, windows, altWindowsOf, skeleton, elevation, tx };
    }
    if (!result) {
      throw new Error(
        `endless planner exhausted retries at segment ${i} (seed ${this.seed})`,
      );
    }
    result.tx.commit();
    const { plan, next, windows, altWindowsOf, skeleton, elevation } = result;
    // Pipe-color chapters (user rule: the pipe color is PER ISLAND): a
    // cell belongs to the most recent island's chapter — islands opened
    // in this segment take over from their run start; earlier cells
    // carry the chapter the line entered with.
    const entryChapter = this.state.islandCount - 1;
    const cellChapter = (idx: number): number => {
      let ch = entryChapter;
      for (const c of plan.crossings) {
        if (c.start <= idx && c.id > ch) ch = c.id;
      }
      return ch;
    };
    this.state = next;
    // Ground vocabulary (an island top IS local ground): solids come back
    // where a window sits on its terrace, and the solid neighbor check may
    // lean on the island deck (the planner's own mass-claim record).
    const deckMap = new Map<string, number>();
    for (const c of plan.crossings) {
      for (const [dx, dz, top] of c.deckCells ?? []) {
        deckMap.set(`${dx},${dz}`, top);
      }
    }
    const localGroundY = (idx: number): number | null => {
      for (const c of plan.crossings) {
        if (idx >= c.start && idx < c.start + c.len) {
          const base = plan.cells[idx].baseY;
          return base === c.profile[idx - c.start] ? base : null;
        }
      }
      return null;
    };
    const assembled = assemble(skeleton, elevation, this.style, windows, {
      open: true,
      localGroundY,
      deckAt: (k) => deckMap.get(k),
    });
    // Island furniture: stub sidings / fans on the planner's ledge cells
    // (mutates stub hosts straight → split in place).
    const furniture = planIslandFurniture(
      rngFamily(this.seed, `furniture/${i}`),
      plan,
      windows,
      assembled.placements,
      // M21: the zone's base weather leans the wind vocabulary.
      segmentClimate(this.seed, i).weather,
    );

    // TAKEABLE SPUR DROPS (loop-mode takeable.ts wired into the void):
    // the planner pinned the host to a 1-cell straight window; here the
    // host placement mutates straight → split (the junction's main
    // channel IS the host's channel; a right-hand branch renders the
    // native mesh mirrored — the takeable.ts move), the branch cell gets
    // its end-hole diving board, and the host window's loop segment
    // gains a ONE-WAY alt centerline out over the hole, flagged `spur`
    // so the collision proxies emit the branch deck with the hole OPEN:
    // a marble riding the branch genuinely falls through and lands on
    // the segment's own receiver channel Δ exactly 1.0 below.
    const spurPieces: { placement: Placement; host: number }[] = [];
    for (const spur of plan.spurs) {
      const wi = windows.findIndex((w) => w.start === spur.host && w.len === 1);
      if (wi < 0) continue; // reserved by the planner — always found
      const ls = assembled.loop.segments[wi];
      const hp = ls.placementIndex >= 0
        ? assembled.placements[ls.placementIndex]
        : undefined;
      if (!hp || hp.pieceId !== "straight") continue;
      const cell = plan.cells[spur.host];
      hp.pieceId = "split";
      if (spur.dir === turnRight(cell.dirIn)) hp.mirrored = true;
      const [wx, wz] = DIR_STEP[spur.dir];
      const bx = cell.x + wx;
      const bz = cell.z + wz;
      spurPieces.push({
        placement: {
          pieceId: spur.cap,
          pos: [bx, cell.baseY, bz],
          rotation: ((1 - spur.dir + 4) % 4) as Dir,
          role: "track",
        },
        host: spur.host,
      });
      // The alt: entry port → rounded pull-off at the junction → out to
      // the hole center → STRAIGHT DOWN the shaft. No return leg (loop
      // mode's teardrop): the fall is guaranteed. The dive is part of
      // the line ON PURPOSE — the wishdir autopilot steers toward a look
      // station ahead, and without shaft stations the look target
      // crosses back to the main exit port and PARKS the marble a
      // length short of the lip (found the hard way on spur-scan-7).
      // railForSegment then skips the main hook after the dive, so the
      // stations resume at the receiver window directly below.
      const floor = cell.baseY + 0.25;
      const [fx, fz] = DIR_STEP[cell.dirIn];
      const cx = cell.x;
      const cz = cell.z;
      const curve: CurveSeg[] = [
        {
          kind: "line",
          a: [cx - fx / 2, floor, cz - fz / 2],
          b: [cx - fx * 0.1, floor, cz - fz * 0.1],
        },
        {
          kind: "cubic",
          p: [
            [cx - fx * 0.1, floor, cz - fz * 0.1],
            [cx, floor, cz],
            [cx + wx * 0.18, floor, cz + wz * 0.18],
            [cx + wx * 0.35, floor, cz + wz * 0.35],
          ] as [Vec3, Vec3, Vec3, Vec3],
        },
        {
          kind: "line",
          a: [cx + wx * 0.35, floor, cz + wz * 0.35],
          b: [bx, floor, bz],
        },
        {
          kind: "line",
          a: [bx, floor, bz],
          b: [bx, floor - 1, bz],
        },
      ];
      ls.alt = {
        curve,
        length: curve.reduce((acc, s) => acc + segLength(s), 0),
        spur: true,
      };
    }

    // BRAIDED PATHS (M17): the planner pinned fork + merge to 1-cell
    // straight windows. Here (a) both host placements mutate straight →
    // split — the fork branches toward the alt side (right-hand branch
    // renders mirrored, the takeable.ts move); the merge is the SAME
    // split z-flipped in its local frame (rotation + 2 with the mirror
    // flag inverted — R(r+2)·M_x ≡ R(r)·M_z), so its branch mouth faces
    // the arriving alt lane and the branch curve sweeps INTO downstream;
    // (b) the alt sub-walk assembles into its own open path (altPath —
    // plain channel proxies hang on it, client + drive harness); (c) the
    // fork window's LoopSegment gains the full alt chain: pull-off
    // through the fork junction, the whole alt lane, pull-in through the
    // merge junction — railForSegment(seg, true) rides the braid's other
    // lane and skips the main windows underneath it (resuming at the
    // window that starts at the merge exit port).
    const braidPieces: { placement: Placement; host: number }[] = [];
    const braidAltLoops: LoopPath[] = [];
    for (const [bi, braid] of plan.braids.entries()) {
      const altWindows = altWindowsOf[bi];
      if (!altWindows) break; // realized with the plan — always present
      const forkIdx = braid.start;
      const mergeIdx = braid.start + braid.len - 1;
      // V3: an OPEN braid has no merge this segment; a CONT braid has no
      // fork (it resolves the previous segment's open fork). The host
      // ls for the alt chain is the fork window, or — for a cont — the
      // FIRST window (the chain enters at the segment boundary).
      const wi = braid.cont
        ? 0
        : windows.findIndex((w) => w.start === forkIdx && w.len === 1);
      const wj = braid.open
        ? -1
        : windows.findIndex((w) => w.start === mergeIdx && w.len === 1);
      if (wi < 0 || (!braid.open && wj < 0)) continue; // planner-reserved
      const lsFork = assembled.loop.segments[wi];
      if (!braid.cont) {
        const hpF = lsFork.placementIndex >= 0
          ? assembled.placements[lsFork.placementIndex]
          : undefined;
        if (!hpF || hpF.pieceId !== "straight") continue;
        const fork = plan.cells[forkIdx];
        hpF.pieceId = "split";
        if (braid.side === turnRight(fork.dirIn)) hpF.mirrored = true;
      }
      if (!braid.open) {
        const lsMerge = assembled.loop.segments[wj];
        const hpM = lsMerge.placementIndex >= 0
          ? assembled.placements[lsMerge.placementIndex]
          : undefined;
        if (!hpM || hpM.pieceId !== "straight") continue;
        const merge = plan.cells[mergeIdx];
        hpM.pieceId = "split";
        hpM.rotation = ((hpM.rotation + 2) % 4) as Dir;
        if (braid.side === turnLeft(merge.dirIn)) hpM.mirrored = true;
        lsMerge.braidMerge = true;
      }

      const altSkeleton: Skeleton = {
        cells: braid.altCells,
        crossings: [],
        helixLoops: [],
      };
      const altElevation: ElevationPlan = {
        baseY: braid.altCells.map((c) => c.baseY),
        reservations: [],
        maxBaseY: Math.max(...braid.altCells.map((c) => c.baseY)),
      };
      const altAssembled = assemble(
        altSkeleton,
        altElevation,
        this.style,
        altWindows,
        { open: true },
      );
      // Alt-lane zone tags (M19b): a tagged alt window is one that lies
      // wholly ≥2 chain cells from BOTH junction mouths (the lip law).
      const chainN = braid.altCells.length;
      const altZoneOfWindow = (
        win: { start: number; len: number },
      ): "icy" | "boost" | null => {
        const lm = braid.laneMods?.alt;
        if (!lm) return null;
        // Lip margins exist only at ends that ARE junctions (V3): an
        // open braid's far end and a cont braid's near end are segment
        // boundaries, not mouths.
        const nearOk = braid.cont ? true : win.start >= 2;
        const farOk = braid.open ? true : win.start + win.len <= chainN - 2;
        return nearOk && farOk ? lm : null;
      };
      // Tagged windows recolor their placements too (zones must READ).
      const altSegs = altAssembled.loop.segments;
      const altZone = new Array<string | null>(
        altAssembled.placements.length,
      ).fill(null);
      for (let k = 0; k < altSegs.length; k++) {
        const startIdx = altSegs[k].placementIndex;
        if (startIdx < 0) continue;
        const z = altZoneOfWindow(altWindows[k]);
        if (!z) continue;
        let end = altAssembled.placements.length;
        for (let k2 = k + 1; k2 < altSegs.length; k2++) {
          if (altSegs[k2].placementIndex >= 0) {
            end = altSegs[k2].placementIndex;
            break;
          }
        }
        for (let p = startIdx; p < end; p++) altZone[p] = z;
      }
      for (const [pi, p] of altAssembled.placements.entries()) {
        braidPieces.push({
          placement: altZone[pi] ? { ...p, zone: altZone[pi]! } : p,
          host: forkIdx,
        });
      }
      braidAltLoops.push(altAssembled.loop);

      // The alt chain (V3 cases): a CLOSED braid runs fork entry port →
      // junction pull-off → alt lane → merge pull-in → merge exit port.
      // An OPEN braid ends with the alt lane at the boundary (no tail);
      // a CONT chain starts with the alt lane at the boundary (no head).
      // Boundary ports are exact, so consecutive segments' chains meet
      // the way main-channel windows do.
      const forkCell = plan.cells[forkIdx];
      const mergeCell = plan.cells[mergeIdx];
      const floor = forkCell.baseY + 0.25;
      const [fx, fz] = DIR_STEP[forkCell.dirIn];
      const [wx, wz] = DIR_STEP[braid.side];
      const head: CurveSeg[] = braid.cont ? [] : [
        {
          kind: "line",
          a: [forkCell.x - fx / 2, floor, forkCell.z - fz / 2],
          b: [forkCell.x - fx * 0.1, floor, forkCell.z - fz * 0.1],
        },
        {
          kind: "cubic",
          p: [
            [forkCell.x - fx * 0.1, floor, forkCell.z - fz * 0.1],
            [forkCell.x, floor, forkCell.z],
            [forkCell.x + wx * 0.28, floor, forkCell.z + wz * 0.28],
            [forkCell.x + wx * 0.5, floor, forkCell.z + wz * 0.5],
          ] as [Vec3, Vec3, Vec3, Vec3],
        },
      ];
      const tail: CurveSeg[] = braid.open ? [] : [
        {
          kind: "cubic",
          p: [
            [mergeCell.x + wx * 0.5, floor, mergeCell.z + wz * 0.5],
            [mergeCell.x + wx * 0.28, floor, mergeCell.z + wz * 0.28],
            [mergeCell.x, floor, mergeCell.z],
            [mergeCell.x + fx * 0.1, floor, mergeCell.z + fz * 0.1],
          ] as [Vec3, Vec3, Vec3, Vec3],
        },
        {
          kind: "line",
          a: [mergeCell.x + fx * 0.1, floor, mergeCell.z + fz * 0.1],
          b: [mergeCell.x + fx / 2, floor, mergeCell.z + fz / 2],
        },
      ];
      const curve: CurveSeg[] = [
        ...head,
        ...altAssembled.loop.segments.flatMap((s) => s.curve),
        ...tail,
      ];
      // Per-part zone tags aligned with the chain curve: junction parts
      // untagged at whichever ends exist.
      const zones: (string | null)[] = head.map(() => null);
      for (let k = 0; k < altSegs.length; k++) {
        const z = altZoneOfWindow(altWindows[k]);
        for (let c = 0; c < altSegs[k].curve.length; c++) zones.push(z);
      }
      for (const _ of tail) zones.push(null);
      lsFork.alt = {
        curve,
        length: curve.reduce((acc, s) => acc + segLength(s), 0),
        braid: true,
        ...(braid.cont ? { contBraid: true } : {}),
        ...(braid.open ? { openBraid: true } : {}),
        ...(zones.some((z) => z !== null) ? { zones } : {}),
      };
    }

    // Islands anchored in this segment.
    const islands = plan.crossings.map((crossing) =>
      buildIsland(
        rngFamily(this.seed, `island/${crossing.id}`),
        crossing,
        plan.cells
          .slice(crossing.start, crossing.start + crossing.len)
          .map((c) => [c.x, c.z] as const),
        this.occ,
        this.style,
        this.catalog,
      )
    );

    // Planner zone kind per window (mods are cell-indexed; windows and
    // path segments are aligned 1:1). Overlaps resolve by PRIORITY, not
    // plan order: gap kinds are structural (they drop geometry), boost
    // must win over a preceding chute's shock zone bleeding into a jump
    // approach, shock is cosmetic damping.
    const MOD_PRIORITY: readonly string[] = [
      "jump",
      "propeller",
      "reorient",
      "spur",
      "braid",
      "boost",
      "gravity",
      "icy",
      "shock",
    ];
    const windowMods = windows.map((w) => {
      let best: string | null = null;
      let bestRank: number = MOD_PRIORITY.length;
      for (const m of plan.mods) {
        if (w.start >= m.start + m.len || m.start >= w.start + w.len) {
          continue;
        }
        const rank = MOD_PRIORITY.indexOf(m.kind);
        if (rank < bestRank) {
          bestRank = rank;
          best = m.kind;
        }
      }
      return best;
    });
    // M19b lane mods (TAG-ONLY law): stamp the braid interior's main-
    // lane windows with the drawn surface — never via plan.mods
    // (realize's exclude rules must not see lane tags: geometry is
    // sacred) and never within 2 cells of fork or merge (the junction-
    // lip law in friction form).
    for (const braid of plan.braids) {
      const main = braid.laneMods?.main;
      if (!main) continue;
      // V3: lip margins only at real junction ends (see altZoneOfWindow).
      const lo = braid.cont ? braid.start : braid.start + 2;
      const hi = braid.open
        ? braid.start + braid.len - 1
        : braid.start + braid.len - 3;
      for (let wi = 0; wi < windows.length; wi++) {
        const w = windows[wi];
        if (
          windowMods[wi] === null && w.start >= lo &&
          w.start + w.len - 1 <= hi
        ) {
          windowMods[wi] = main;
        }
      }
    }
    // JUMP gaps: the reserved gap window realized a piece (the WFC needs
    // the elevation step to be honest), but the set-piece is the ABSENCE
    // of that piece — drop its placements so nothing renders in the gap
    // and the collision proxies (which skip "jump" path segments) match
    // what the eye sees. Placements are emitted in window order, so a
    // window's span runs from its placementIndex to the next window's.
    const dropped = new Set<number>();
    const chapterOf = new Array<number>(assembled.placements.length).fill(
      entryChapter,
    );
    const zoneOf = new Array<string | null>(assembled.placements.length).fill(
      null,
    );
    const loopSegs = assembled.loop.segments;
    for (let wi = 0; wi < loopSegs.length; wi++) {
      const startIdx = loopSegs[wi].placementIndex;
      if (startIdx < 0) continue;
      let end = assembled.placements.length;
      for (let wj = wi + 1; wj < loopSegs.length; wj++) {
        if (loopSegs[wj].placementIndex >= 0) {
          end = loopSegs[wj].placementIndex;
          break;
        }
      }
      const ch = cellChapter(windows[wi].start);
      for (let k = startIdx; k < end; k++) {
        chapterOf[k] = ch;
        if (
          windowMods[wi] === "jump" || windowMods[wi] === "propeller" ||
          windowMods[wi] === "reorient"
        ) {
          // Reorient runs drop their kit pieces too — the corkscrewing
          // collision ribbon is rendered directly (visual == collision).
          dropped.add(k);
        }
        if (
          windowMods[wi] === "icy" || windowMods[wi] === "gravity" ||
          windowMods[wi] === "boost"
        ) {
          // Boost joined in M19b (lane contrast must READ) — sprint
          // zones recolor with it: one coherent presentation.
          zoneOf[k] = windowMods[wi];
        }
      }
    }

    // Zone pad visuals (M11): set-pieces must READ on screen. Banners
    // stand on the channel rails (the goal-gate measurement — Kenney
    // designed the posts to sit on them), so they work even in the void:
    // banner-high marks a jump lip, banner-double-high a propeller lip,
    // a plain banner the entry of a standalone boost zone. Rule-derived,
    // no RNG. A banner goes ONLY on a plain straight window (USER RULE):
    // never on curved-channel cells (waves/wiggles are F-moves but the
    // channel swings away from the posts) and never under a tunnel arch
    // (the banner would thread the arch mesh) — family "straight" is
    // exactly the windows with a bare centered channel.
    const windowFamilyAt = (idx: number): string | null => {
      for (const w of windows) {
        if (idx >= w.start && idx < w.start + w.len) return w.def.family;
      }
      return null;
    };
    const gapStarts = new Set(
      plan.mods
        .filter((m) => m.kind === "jump" || m.kind === "propeller")
        .map((m) => m.start),
    );
    const pads: Placement[] = [];
    for (const m of plan.mods) {
      if (m.kind === "jump" || m.kind === "propeller") {
        const lip = plan.cells[m.start - 1];
        if (
          lip && lip.move === "F" &&
          windowFamilyAt(m.start - 1) === "straight"
        ) {
          pads.push({
            pieceId: m.kind === "propeller"
              ? "banner-double-high"
              : "banner-high",
            pos: [lip.x, lip.baseY + 0.5, lip.z],
            rotation: ((1 - lip.dirIn + 4) % 4) as Dir,
            role: "track",
            chapter: cellChapter(m.start - 1),
          });
        }
      } else if (m.kind === "boost" || m.kind === "gravity") {
        // The lip banner already announces a jump's approach zone.
        if (gapStarts.has(m.start + m.len)) continue;
        for (
          let idx = m.start;
          idx < m.start + m.len && idx < plan.cells.length;
          idx++
        ) {
          const c = plan.cells[idx];
          if (c.move !== "F" || windowFamilyAt(idx) !== "straight") continue;
          pads.push({
            pieceId: "banner",
            pos: [c.x, c.baseY + 0.5, c.z],
            rotation: ((1 - c.dirIn + 4) % 4) as Dir,
            role: "track",
            chapter: cellChapter(idx),
          });
          break;
        }
      }
    }

    // Slalom rule (M8): the generator plans in track cells; the WORLD sees
    // track pieces at ×TRACK_SCALE (position and mesh scale) while islands
    // and decor stay 1×. The centerline scales with the track.
    // Filtering jump/reorient meshes changes array indices. Remap every
    // surviving path window to the FINAL placement array so diagnostic
    // provenance (and the LoopPath contract) cannot accidentally name a
    // later support/decor placement after an earlier gap was removed.
    const placementRemap = new Map<number, number>();
    const mainPlacements: Placement[] = [];
    for (const [idx, p] of assembled.placements.entries()) {
      if (dropped.has(idx)) continue;
      placementRemap.set(idx, mainPlacements.length);
      mainPlacements.push({
        ...p,
        chapter: chapterOf[idx],
        ...(zoneOf[idx] ? { zone: zoneOf[idx]! } : {}),
      });
    }
    const placements = [
      ...mainPlacements,
      ...furniture.map(({ placement, host }): Placement => ({
        ...placement,
        chapter: cellChapter(host),
      })),
      ...spurPieces.map(({ placement, host }): Placement => ({
        ...placement,
        chapter: cellChapter(host),
      })),
      ...braidPieces.map(({ placement, host }): Placement => ({
        ...placement,
        chapter: cellChapter(host),
      })),
      ...pads,
    ].map((p): Placement => ({
      ...p,
      pos: scaleVec(p.pos),
      scale: TRACK_SCALE,
    }));
    const path: LoopPath = {
      segments: assembled.loop.segments.map((s) => {
        const curve = s.curve.map(scaleSeg);
        const length = s.length * TRACK_SCALE;
        // Diamond/spur/braid branches: the alt centerline scales with
        // the track too (the markers ride along for the proxies).
        const alt = s.alt
          ? {
            curve: s.alt.curve.map(scaleSeg),
            length: s.alt.length * TRACK_SCALE,
            ...(s.alt.spur ? { spur: true } : {}),
            ...(s.alt.braid ? { braid: true } : {}),
            ...(s.alt.zones ? { zones: s.alt.zones } : {}),
            ...(s.alt.contBraid ? { contBraid: true } : {}),
            ...(s.alt.openBraid ? { openBraid: true } : {}),
          }
          : undefined;
        return {
          ...s,
          placementIndex: s.placementIndex >= 0
            ? placementRemap.get(s.placementIndex) ?? -1
            : -1,
          curve,
          length,
          ...(alt ? { alt } : {}),
          // Floor-pierce centers scale with the track (LoopSegment law).
          ...(s.holes ? { holes: s.holes.map(scaleVec) } : {}),
        };
      }),
      totalLength: assembled.loop.totalLength * TRACK_SCALE,
    };
    // Braid alt LANE, world-scaled — the client (and drive harness)
    // append plain channel proxies over it (placementIndex −1: its
    // meshes ride in `placements` already, chapter-stamped).
    // M23: EVERY braid lane's loop rides altPath (two braids per segment
    // concatenate — channel ribbons per window, spans disjoint).
    const altPath: LoopPath | undefined = braidAltLoops.length > 0
      ? {
        segments: braidAltLoops.flatMap((lp) =>
          lp.segments.map((s) => ({
            placementIndex: -1,
            curve: s.curve.map(scaleSeg),
            length: s.length * TRACK_SCALE,
          }))
        ),
        totalLength: braidAltLoops.reduce((a, lp) => a + lp.totalLength, 0) *
          TRACK_SCALE,
      }
      : undefined;

    // Gravity reorientation (M16): a reorient run ROLLS everything
    // downstream about its travel axis. The cumulative presentation
    // frame is composed here — culling never rewinds generation, so the
    // running frame is as seed-pure as nextStartS. Plan data stays
    // +y-up; only the stamped frame record tells the client how this
    // segment PRESENTS.
    const entryFrame = this.frame;
    let frame: SegmentFrame | undefined;
    const reorient = plan.mods.find((m) => m.kind === "reorient");
    if (reorient && reorient.angle !== undefined) {
      const from = windows.findIndex((w) => w.start === reorient.start);
      let to = from;
      while (
        to + 1 < windows.length &&
        windows[to + 1].start < reorient.start + reorient.len
      ) to++;
      if (from < 0) {
        throw new Error(
          `reorient window missing at segment ${i} (seed ${this.seed})`,
        );
      }
      let len = 0;
      for (let k = from; k <= to; k++) len += path.segments[k].length;
      const first = path.segments[from].curve[0];
      const p0 = segPoint(first, 0);
      const cell = plan.cells[reorient.start];
      const [dx, dz] = DIR_STEP[cell.dirIn];
      const axis: [number, number, number] = [dx, 0, dz];
      const pivot: [number, number, number] = [p0[0], p0[1], p0[2]];
      const exit = composeTwist(
        entryFrame,
        quatRotate(entryFrame.q, axis),
        reorient.angle,
        affineApply(entryFrame, pivot),
      );
      frame = {
        entry: entryFrame,
        exit,
        twist: { angle: reorient.angle, axis, pivot, len, from, to },
      };
      this.frame = exit;
    } else if (!isIdentityAffine(entryFrame)) {
      frame = { entry: entryFrame, exit: entryFrame };
    }

    this.segments.push({
      index: i,
      plan,
      pieceFamilies: windows.map((w) => w.def.family),
      windowRanges: windows.map((w) => ({ start: w.start, len: w.len })),
      windowMods,
      placements,
      path,
      ...(altPath ? { altPath } : {}),
      islands,
      // Void decor last (M18b): its own labeled family, consuming
      // nothing from any stream above — and fed the finished segment.
      voidDecor: planVoidDecor(this.seed, i, path, altPath, islands, this.occ),
      // V3: a continuation's fork is always the immediately previous
      // segment (open forks resolve at the very next boundary; the CAP
      // bounds the total span, not the segment count).
      ...(plan.braids.some((b) => b.cont) ? { braidContOf: i - 1 } : {}),
      startS: this.nextStartS,
      ...(frame ? { frame } : {}),
    });
    this.nextStartS += path.totalLength;
    this.cullBefore(i - KEEP_SEGMENTS);
  }
}
